import { notEmpty } from '@frontend/utils';
import { useState, useRef, useEffect } from 'react';

import {
  PageFields_CurrenciesPage_Fragment as CurrenciesPageFields,
  BlockchainData,
} from '../../../graphql/generated';

interface useUpdateCurrenciesOnExpirationProps {
  currencies: CurrenciesPageFields['pageData']['cryptoCurrencies'];
  expiresAt: CurrenciesPageFields['pageData']['expiresAt'];
}

const useUpdateCurrenciesOnExpiration = ({
  currencies: initialCurrencies,
  expiresAt,
}: useUpdateCurrenciesOnExpirationProps) => {
  const [currencies, setCurrencies] = useState<typeof initialCurrencies | []>(
    initialCurrencies
  );
  const allUniqueMinExchangeRateTimers = useRef<number[] | null>(null);

  const isThereEnoughTime = (time: number) => {
    return expiresAt * 1000 - Date.now() > time;
  };

  if (!allUniqueMinExchangeRateTimers.current) {
    const allTimers =
      currencies
        ?.flatMap((c) => c?.blockchains?.map((b) => b?.minExchangeRateTimer))
        .filter(notEmpty) || [];

    allUniqueMinExchangeRateTimers.current = [...new Set(allTimers)];
  }

  const checkBlockchainHasEnoughTime = (blockchain: BlockchainData | null) => {
    return (
      blockchain?.minExchangeRateTimer &&
      isThereEnoughTime(blockchain.minExchangeRateTimer)
    );
  };

  const updateBlockchainAvailability = (blockchain: BlockchainData | null) => {
    if (!blockchain) {
      return null;
    }

    if (!blockchain.available) {
      return blockchain;
    }

    if (!checkBlockchainHasEnoughTime(blockchain)) {
      return {
        ...blockchain,
        available: false,
        reason: 'Payment expires soon',
      };
    }

    return blockchain;
  };

  const updateCurrencies = (
    currencies: CurrenciesPageFields['pageData']['cryptoCurrencies']
  ) => {
    if (!currencies) {
      return [];
    }

    return currencies.map((currency) => ({
      ...currency,
      blockchains:
        currency?.blockchains?.map(updateBlockchainAvailability) || [],
    }));
  };

  useEffect(() => {
    const interval = setInterval(() => {
      const timers = allUniqueMinExchangeRateTimers.current;

      if (!timers) {
        return;
      }

      const expiredTimer = timers.find((t) => !isThereEnoughTime(t));

      if (!expiredTimer) return;

      const updatedCurrencies = updateCurrencies(currencies);

      setCurrencies((updatedCurrencies as typeof initialCurrencies) || []);
      allUniqueMinExchangeRateTimers.current = timers.filter(isThereEnoughTime);
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  return {
    currencies,
  };
};

export default useUpdateCurrenciesOnExpiration;
